import SectionWrapper from '@/components/feature/SectionWrapper';
import SEO, { siteUrl } from '@/components/feature/SEO';

const WHATSAPP_URL = 'https://wa.me/923179699957?text=Hi%20Moin%20Khan%20Boxing%20Academy!%20I%20want%20to%20join%20training';

const stats = [
  { value: '10+', label: 'Years Experience' },
  { value: '500+', label: 'Students Trained' },
  { value: '15+', label: 'Championships' },
  { value: '4', label: 'Training Programs' },
];

const achievements = [
  'International Boxing Experience',
  'Certified Professional Coach',
  'Multiple Championship Wins',
  'Elite Training Methodology',
  '500+ Student Transformations',
  'All Levels Welcome',
];

const historyPoints = [
  {
    title: 'Early Career',
    desc: 'Started boxing at a young age with relentless dedication. Trained under top coaches in Pakistan and developed a powerful competitive foundation.',
  },
  {
    title: 'International Experience',
    desc: 'Competed in international boxing circuits, gaining real fight experience against top-level opponents from around the world.',
  },
  {
    title: 'Championship Achievements',
    desc: 'Won 15+ championships across national and international levels. Built a reputation as one of the most skilled and disciplined fighters in the region.',
  },
  {
    title: 'Coaching Legacy',
    desc: 'Transitioned from competitor to coach, bringing real fight knowledge into training. Over 500 students trained with a focus on results and discipline.',
  },
];

export default function About() {
  return (
    <main>
      <SEO
        title="About Moin Khan Boxing Academy — International Boxing Champion & Coach"
        description="Learn about Moin Khan, an international boxing champion with 10+ years experience, 500+ students trained, and 15+ championships. Discover our story of discipline, coaching, and results in Islamabad."
        keywords="about Moin Khan, boxing champion Pakistan, boxing coach Islamabad, Moin Khan boxing academy story, international boxer Pakistan"
        canonicalUrl={siteUrl ? `${siteUrl}/about` : undefined}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            '@id': `${siteUrl}/about#page`,
            url: `${siteUrl}/about`,
            name: 'About Moin Khan Boxing Academy',
            description: 'Built on discipline, driven by passion, proven by results. Moin Khan Boxing Academy offers elite boxing training in Islamabad.',
            mainEntity: {
              '@type': 'Person',
              name: 'Moin Khan',
              jobTitle: 'Head Coach / International Boxing Champion',
              description: 'International Boxing Champion with over a decade of competitive experience. Founder and head coach at Moin Khan Boxing Academy.',
              affiliation: { '@type': 'Organization', name: 'Moin Khan Boxing Academy', '@id': `${siteUrl}/#organization` },
            },
          },
        ]}
      />
      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20portrait%20of%20a%20professional%20boxer%20standing%20confidently%20in%20a%20boxing%20gym%2C%20dramatic%20red%20rim%20lighting%20on%20black%20background%2C%20smoke%20atmosphere%2C%20wearing%20boxing%20gloves%2C%20intense%20determined%20expression%2C%20professional%20sports%20photography%2C%20high%20contrast%2C%20premium%20moody%20aesthetic&width=1920&height=700&seq=about-hero-3&orientation=landscape"
            alt="Moin Khan"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-boxing-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-boxing-black via-transparent to-boxing-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <SectionWrapper>
            <span className="section-label">/ The Academy</span>
            <h1 className="heading-mixed text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              About the <span className="text-highlight">Academy</span>
            </h1>
            <p className="font-poppins text-white/60 text-sm md:text-base max-w-2xl mx-auto mt-4">
              Built on discipline, driven by passion, proven by results.
            </p>
          </SectionWrapper>
        </div>
      </section>

      {/* Moin Khan History - CREATIVE TOP SECTION */}
      <section className="bg-boxing-black py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            {/* Image with overlap styling */}
            <div className="w-full lg:w-1/2 relative">
              <SectionWrapper animation="slide-in-left">
                <div className="relative">
                  {/* Decorative red accent block */}
                  <div className="absolute -top-4 -left-4 w-24 h-24 bg-boxing-red/15 rounded-xl -z-10" />
                  <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-boxing-red/10 rounded-xl -z-10" />
                  <div className="rounded-xl overflow-hidden border border-white/5">
                    <img
                      src="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/e45f316ea85d5f4030e426237dbf23c5.png"
                      alt="Moin Khan Champion"
                      className="w-full h-72 md:h-96 object-cover object-top"
                    />
                  </div>
                </div>
              </SectionWrapper>
            </div>
            <div className="w-full lg:w-1/2">
              <SectionWrapper animation="slide-in-right" delay={150}>
                <span className="section-label">/ The Champion</span>
                <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl mb-5">
                  Moin <span className="text-highlight">Khan</span>
                </h2>
                <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6">
                  Moin Khan is an International Boxing Champion with over a decade of competitive experience. From humble beginnings to competing on the world stage, his journey is defined by discipline, resilience, and an unbreakable will to win.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {historyPoints.map((point) => (
                    <div key={point.title} className="card-glow bg-boxing-darker rounded-xl p-5">
                      <h4 className="font-oswald uppercase text-sm tracking-wide text-white mb-2">
                        {point.title}
                      </h4>
                      <p className="text-boxing-textgray text-xs md:text-sm leading-relaxed">
                        {point.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </SectionWrapper>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-boxing-darker py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row-reverse gap-10 lg:gap-16 items-center">
            {/* Image with creative container */}
            <div className="w-full lg:w-1/2 relative">
              <SectionWrapper animation="slide-in-right">
                <div className="relative">
                  <div className="absolute -top-4 -right-4 w-28 h-28 bg-boxing-red/15 rounded-xl -z-10" />
                  <div className="rounded-xl overflow-hidden border border-white/5">
                    <img
                      src="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/8e16a7bb62b91b05664dabb4d78111dd.png"
                      alt="Training session"
                      className="w-full h-72 md:h-96 object-cover object-top"
                    />
                  </div>
                </div>
              </SectionWrapper>
            </div>
            <div className="w-full lg:w-1/2">
              <SectionWrapper animation="slide-in-left" delay={150}>
                <span className="section-label">/ Our Story</span>
                <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl mb-5">
                  Discipline. <span className="text-highlight">Coaching.</span> Results.
                </h2>
                <div className="space-y-3 text-white/70 text-sm md:text-base leading-relaxed">
                  <p>
                    Moin Khan Boxing Academy was founded with a single mission: to bring international-level boxing training to aspiring fighters and fitness enthusiasts. With over 10 years of hands-on experience in competitive boxing, Moin Khan brings real fight knowledge into every session.
                  </p>
                  <p>
                    We do not believe in generic gym routines. Every punch, every drill, every training plan is designed to build real skill, real strength, and real discipline. Whether your goal is to step into the ring, get in the best shape of your life, or instill confidence in your child — this is where transformation happens.
                  </p>
                  <p>
                    The academy has trained over 500 students across all programs, producing multiple champions and countless personal transformations. We welcome beginners, hobbyists, competitive fighters, and everyone in between.
                  </p>
                </div>
              </SectionWrapper>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-boxing-black py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <SectionWrapper className="text-center mb-12 md:mb-16">
            <span className="section-label">/ By the Numbers</span>
            <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl">
              Credibility That <span className="text-highlight">Speaks</span>
            </h2>
          </SectionWrapper>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {stats.map((stat, index) => (
              <SectionWrapper key={stat.label} delay={index * 120}>
                <div className="card-glow bg-boxing-darker rounded-xl p-6 md:p-7 text-center">
                  <span className="font-bebas text-4xl md:text-5xl lg:text-6xl text-white block">
                    {stat.value}
                  </span>
                  <span className="font-oswald uppercase text-xs md:text-sm tracking-[2px] text-white/50 mt-1 block">
                    {stat.label}
                  </span>
                </div>
              </SectionWrapper>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="bg-boxing-darker py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <SectionWrapper className="text-center mb-12 md:mb-16">
            <span className="section-label">/ What Sets Us Apart</span>
            <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl">
              Why We Are <span className="text-highlight">Different</span>
            </h2>
          </SectionWrapper>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {achievements.map((item, index) => (
              <SectionWrapper key={item} delay={index * 100}>
                <div className="card-glow flex items-center gap-4 bg-boxing-black rounded-xl p-5 md:p-6">
                  <span className="w-10 h-10 flex items-center justify-center bg-boxing-red/10 rounded-lg shrink-0">
                    <i className="ri-medal-line text-boxing-red text-lg" />
                  </span>
                  <span className="font-oswald uppercase text-sm md:text-base tracking-wide text-white">
                    {item}
                  </span>
                </div>
              </SectionWrapper>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20moody%20boxing%20ring%20ropes%20closeup%20with%20dramatic%20cinematic%20lighting%2C%20deep%20red%20spotlights%20on%20black%20background%2C%20smoke%20atmosphere%2C%20professional%20sports%20photography%2C%20high%20contrast%2C%20premium%20boxing%20gym%20aesthetic%2C%20intense%20atmosphere&width=1920&height=600&seq=about-cta-3&orientation=landscape"
            alt="Boxing ring"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-boxing-black/80" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <SectionWrapper>
            <h2 className="heading-mixed text-4xl sm:text-5xl md:text-6xl">
              Ready to Train With
              <br />
              <span className="text-highlight">the Best?</span>
            </h2>
            <p className="text-white/60 text-sm md:text-base mt-5 mb-8">
              Your first session could be today. Message us and let us get started.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm md:text-base px-10 py-4"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg" />
              </span>
              Join Now via WhatsApp
            </a>
          </SectionWrapper>
        </div>
      </section>
    </main>
  );
}
